import { EntityType, ZoneAuxList } from 'src/api'
import { TreeItemI } from '../CustomTree'

type TreeItemsMap = { [key: string]: TreeItemI[] }

type OptionalTreeItemProps = Omit<
  TreeItemI,
  'id' | 'label' | 'type' | 'checked'
> & { checked?: boolean }

interface Entity {
  name: string
  id: string
  entityType: EntityType
}

const mergeTreeItemsMap = (
  treeItemsMap: TreeItemsMap | TreeItemsMap[] | undefined
) => {
  let map: TreeItemsMap | undefined
  if (Array.isArray(treeItemsMap)) {
    map = treeItemsMap.reduce((acc, treeItemsMap) => {
      Object.entries(treeItemsMap).forEach(([key, value]) => {
        acc[key] = [...(acc[key] ?? []), ...value]
      })
      return acc
    }, {} as TreeItemsMap)
  } else {
    map = treeItemsMap
  }
  return map
}

const entityToTreeItem = <T extends any>({
  entity,
  checked = false,
  treeItemMapper,
  ...treeItemProps
}: {
  entity: Entity
  treeItemMapper?: (entity: T) => Partial<TreeItemI>
} & OptionalTreeItemProps): TreeItemI => {
  return {
    id: entity.id,
    label: entity.name,
    type: entity.entityType,
    checked,
    ...treeItemProps,
    ...(treeItemMapper && treeItemMapper(entity as T))
  }
}

const entityListToTreeItem = <T extends any>({
  entityList,
  checkedIds,
  treeItemsMap
}: {
  entityList: Entity[]
  checkedIds?: string[]
  treeItemsMap?: TreeItemsMap | TreeItemsMap[]
  treeItemMapper?: (entity: T) => TreeItemI
}) => {
  const map = mergeTreeItemsMap(treeItemsMap)
  return entityList.map((entity) => {
    const checked = !!checkedIds?.includes(entity.id)
    return entityToTreeItem({
      entity,
      checked,
      treeItems: map?.[entity.id]
    })
  })
}

const entityListToTreeItemsMap = <T extends Entity>({
  entityList,
  idParamToMap,
  checkedIds,
  treeItemsMap,
  treeItemMapper
}: {
  entityList: T[]
  checkedIds?: string[]
  idParamToMap: keyof T
  treeItemsMap?: TreeItemsMap | TreeItemsMap[]
  treeItemMapper?: (entity: T) => Partial<TreeItemI>
}) => {
  const map = mergeTreeItemsMap(treeItemsMap)
  return entityList.reduce((acc, entity) => {
    const keyParamValue = entity[idParamToMap] as string
    const alreadyExistMapKey = acc[keyParamValue]
    const entityChecked = !!checkedIds?.includes(entity.id)
    const entityTreeItem = entityToTreeItem({
      entity,
      checked: entityChecked,
      treeItems: map?.[entity.id],
      treeItemMapper
    })
    const newVal = alreadyExistMapKey
      ? [...alreadyExistMapKey, entityTreeItem]
      : [entityTreeItem]
    acc[keyParamValue] = newVal
    return acc
  }, {} as TreeItemsMap)
}

const zoneAuxToTreeItemWithZoneChildren = ({
  zoneAux,
  checkedIds,
  childrenZoneAuxList,
  treeItemsMap
}: {
  zoneAux: ZoneAuxList
  childrenZoneAuxList: ZoneAuxList[]
  treeItemsMap?: TreeItemsMap
  checkedIds?: string[]
}): TreeItemI => {
  let restChildrenZoneAuxList = [...childrenZoneAuxList]
  const currentZoneChildrenZoneTreeItems = childrenZoneAuxList.reduce(
    (acc, childrenZone, index) => {
      if (childrenZone.parentId === zoneAux.id) {
        restChildrenZoneAuxList = [
          ...restChildrenZoneAuxList.slice(0, index),
          ...restChildrenZoneAuxList.slice(index + 1)
        ]
        return [
          ...acc,
          zoneAuxToTreeItemWithZoneChildren({
            zoneAux: childrenZone,
            childrenZoneAuxList: restChildrenZoneAuxList,
            treeItemsMap
          })
        ]
      }
      return acc
    },
    [] as TreeItemI[]
  )
  const currentZoneTreeItemsMapValue = treeItemsMap?.[zoneAux.id] ?? []
  const checked = !!checkedIds?.includes(zoneAux.id)
  return customTreeAdapter.entityToTreeItem({
    entity: zoneAux,
    checked,
    treeItems: [
      ...currentZoneChildrenZoneTreeItems,
      ...currentZoneTreeItemsMapValue
    ]
  })
}

const zoneAuxListToTreeItem = ({
  zoneAuxList,
  checkedIds,
  treeItemsMap
}: {
  zoneAuxList: ZoneAuxList[]
  treeItemsMap?: TreeItemsMap | TreeItemsMap[]
  checkedIds?: string[]
}): TreeItemI[] => {
  const fatherZoneAuxList: ZoneAuxList[] = []
  const childrenZoneAuxList: ZoneAuxList[] = []
  zoneAuxList.forEach((zoneAux) => {
    if (zoneAux.parentId) return childrenZoneAuxList.push(zoneAux)
    fatherZoneAuxList.push(zoneAux)
  })
  const map = mergeTreeItemsMap(treeItemsMap)
  return fatherZoneAuxList.map((fatherZoneAux) =>
    zoneAuxToTreeItemWithZoneChildren({
      zoneAux: fatherZoneAux,
      childrenZoneAuxList,
      checkedIds,
      treeItemsMap: map
    })
  )
}

const zoneAuxListToTreeItemMap = ({
  zoneAuxList,
  checkedIds,
  treeItemsMap,
  idParamToMap
}: {
  zoneAuxList: ZoneAuxList[]
  treeItemsMap?: TreeItemsMap | TreeItemsMap[]
  checkedIds?: string[]
  idParamToMap: keyof ZoneAuxList
}): TreeItemsMap => {
  const fatherZoneAuxList: ZoneAuxList[] = []
  const childrenZoneAuxList: ZoneAuxList[] = []
  zoneAuxList.forEach((zoneAux) => {
    if (zoneAux.parentId) return childrenZoneAuxList.push(zoneAux)
    fatherZoneAuxList.push(zoneAux)
  })
  const map = mergeTreeItemsMap(treeItemsMap)
  return fatherZoneAuxList.reduce((acc, fatherZoneAux) => {
    const zoneAuxParamValue = fatherZoneAux[idParamToMap] as string
    acc[zoneAuxParamValue] = [
      ...(acc[zoneAuxParamValue] ?? []),
      zoneAuxToTreeItemWithZoneChildren({
        zoneAux: fatherZoneAux,
        childrenZoneAuxList,
        checkedIds,
        treeItemsMap: map
      })
    ]
    return acc
  }, {} as TreeItemsMap)
}

export const customTreeAdapter = {
  entityToTreeItem,
  entityListToTreeItem,
  entityListToTreeItemsMap,
  zoneAuxListToTreeItem,
  zoneAuxListToTreeItemMap
}
